import { Injectable, inject } from '@angular/core';
import { Query } from '@datorama/akita';
import { CmgCcwLoanStore } from './ccw-loan.store';
import { DirtyChecker2 } from '../../core/dirty-checker-2';
import {
  AmortizationType,
  DpFactorType,
  FundingFeeType,
  LoanFeaturesModel,
  LoanPurposeType,
  MortgageType,
  OriginationChannel,
  PmiType,
  ReferralInfoModel,
  RelatedLoanModel,
  SectionOfAct,
} from '../../../cmg-1003/models';
import { distinctUntilChanged, filter, map, Observable, of, switchMap } from 'rxjs';
import { CcwLoanDataModel } from '../../../closing-cost-worksheet-15/models/ccw-loan-data-model';
import { LoanPropertyPreliminaryDataBase } from '../../../closing-cost-worksheet-15/models/ccw.models';

@Injectable({ providedIn: 'root' })
export class CmgCcwLoanQuery extends Query<CcwLoanDataModel> {
  protected store: CmgCcwLoanStore;

  dirtyChecker2: DirtyChecker2 = new DirtyChecker2(this.store, this);

  constructor() {
    const store = inject(CmgCcwLoanStore);

    super(store);

    this.store = store;
  }
  selectLoanFeatures(): Observable<LoanFeaturesModel> {
    return this.selectLoanInfo().pipe(
      switchMap((state) => {
        const loanFeatures = {
          isBalloon: state.isBalloon,
          isInterestOnly: state.isInterestOnly,
          isNegativeAmortization: state.isNegativeAmortization,
          isPrepaymentPenalty: state.isPrepaymentPenalty,
          hasPrepaymentPenalty: state.hasPrepaymentPenalty,
          prepaymentPenaltyMax: state.prepaymentPenaltyMax,
          prepaymentPenaltyTerm: state.prepaymentPenaltyTerm,
          isTemporaryBuydown: state.isTemporaryBuydown,
          isOther: state.isOther,
          productDescription: state.productDescription,
          productDescriptionOther: state.productDescriptionOther,
          isRateReboundRefinance: state.isRateReboundRefinance,
          qualRateReboundLoanNumber: state.qualRateReboundLoanNumber,
        } as LoanFeaturesModel;
        return of(loanFeatures);
      }),
    );
  }
  selectLoanInfo(): Observable<CcwLoanDataModel> {
    return this.select().pipe(
      //  Only emit when loanId is something
      filter((state) => !!state.loanId),
    );
  }
  selectLoanOriginationChannel(): Observable<OriginationChannel> {
    return this.selectLoanInfo().pipe(map((state) => state.originationChannel));
  }
  selectLoanTotalLoanAmount(): Observable<number> {
    return this.selectLoanInfo().pipe(map((state) => state.totalLoanAmount));
  }
  selectReferralInfo(): Observable<ReferralInfoModel> {
    return this.selectLoanInfo().pipe(map((state) => state.referralInfo));
  }
  selectRelatedLoans(): Observable<RelatedLoanModel[]> {
    return this.selectLoanInfo().pipe(map((state) => state.relatedLoans));
  }
  selectRateReboundRefinance(): Observable<boolean> {
    return this.selectLoanInfo().pipe(map((state) => state.isRateReboundRefinance));
  }
  selectLoanPropertyPreliminaryDataBase(): Observable<LoanPropertyPreliminaryDataBase> {
    return this.selectLoanInfo().pipe(
      map((state) => {
        const payload: LoanPropertyPreliminaryDataBase = {
          fileDataId: state.fileDataId,
          appraisedValue: state.appraisalValue,
          salesPrice: state.salesPrice,
          dpfactor: state.dpfactor,
          downPaymentPercentage: state.downPaymentPercentage,
          downPaymentAmount: state.downPaymentAmount,
          baseLoanAmount: state.baseLoanAmount,
          intRate: state.interestRate,
          fileCreditScore: state.fileCreditScore,
          pmiType: state.pmiType,
          upfrontMiType: state.upfrontMiType,
        };
        return payload;
      }),
    );
  }
  showPmiType$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.conventional || loan.mortgageAppliedFor === MortgageType.other;
    }),
  );
  showAfterImpValue$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return (
        loan.mortgageAppliedFor === MortgageType.fha && (loan.sectionOfAct === SectionOfAct._203_k || loan.sectionOfAct === SectionOfAct._221_d_2)
      );
    }),
  );
  isFha$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.fha;
    }),
  );
  isFhaRefi$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.fha && loan.loanPurpose === LoanPurposeType.refinance;
    }),
  );
  isFundingFee$ = this.selectLoanInfo().pipe(
    distinctUntilChanged((oldLoan, newLoan) => oldLoan.mortgageAppliedFor === newLoan.mortgageAppliedFor),
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.va;
    }),
  );
  isUSDA$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.usda;
    }),
  );
  isOther$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.other;
    }),
  );
  isConventional$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.conventional;
    }),
  );
  isHeloc$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor === MortgageType.heloc;
    }),
  );
  ltv$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.ltv;
    }),
  );
  mortgageType$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mortgageAppliedFor;
    }),
  );
  upfrontMiFactor$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.upfrontMiFactor;
    }),
  );
  miMonthlyPrem$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.monthlyMiAmount;
    }),
  );
  miPFFFinancingCalcOption$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mipffFinancingCalcOption;
    }),
  );
  mipffPercent$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.mipffPercent;
    }),
  );
  loanProgramCode$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.loanProgramCode;
    }),
  );

  showMonthlyMI$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return (
        (loan.mortgageAppliedFor === MortgageType.va && loan.fundingFeeType !== FundingFeeType.exempt && !!loan.fundingFeeType) ||
        (loan.mortgageAppliedFor === MortgageType.fha && !!loan.caseNumberAssignedDate) ||
        loan.mortgageAppliedFor === MortgageType.usda ||
        (loan.pmiType !== PmiType.none &&
          loan.pmiType !== PmiType.select &&
          loan.pmiType !== PmiType.lenderPaid &&
          (loan.mortgageAppliedFor === MortgageType.conventional || loan.mortgageAppliedFor === MortgageType.other)) ||
        loan.mortgageAppliedFor === MortgageType.heloc
      );
    }),
  );
  showMonthlyMIPaidInCash$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return (
        (loan.mortgageAppliedFor === MortgageType.va && loan.fundingFeeType !== FundingFeeType.exempt && !!loan.fundingFeeType) ||
        (loan.mortgageAppliedFor === MortgageType.fha && !!loan.caseNumberAssignedDate) ||
        loan.mortgageAppliedFor === MortgageType.usda ||
        (loan.pmiType !== PmiType.none &&
          loan.pmiType !== PmiType.select &&
          loan.pmiType !== PmiType.lenderPaid &&
          loan.pmiType !== PmiType.monthly && //Only difference from showMonthlyMIPaidInCash() & showMonthlyMI()
          (loan.mortgageAppliedFor === MortgageType.conventional || loan.mortgageAppliedFor === MortgageType.other)) ||
        loan.mortgageAppliedFor === MortgageType.heloc
      );
    }),
  );
  showRefinancePurpose$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.loanPurpose === LoanPurposeType.refinance;
    }),
  );
  showOtherPurposeDescription$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.loanPurpose === LoanPurposeType.other;
    }),
  );
  showConstructionFields$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.loanPurpose === LoanPurposeType.construction || loan.loanPurpose === LoanPurposeType.constructionPerm;
    }),
  );
  showRateChange$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.amortizationType === AmortizationType.ARM;
    }),
  );
  hasSoldRealEstateIn60Months$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.haveYouSoldRealEstateIn60Months === true;
    }),
  );
  showDownPaymentPercentage$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.dpfactor === DpFactorType.downpaymentPercentage;
    }),
  );
  showDownPaymentAmount$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.dpfactor === DpFactorType.downpaymentAmount;
    }),
  );
  showAmortizationTypeDescription$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.amortizationType === AmortizationType.ARM || loan.amortizationType === AmortizationType.NoPayment;
    }),
  );
  showQualRateOption$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.amortizationType === AmortizationType.ARM;
    }),
  );
  showHomeOwnedThroughVa$ = this.selectLoanInfo().pipe(
    map((loan) => {
      return loan.areYouDisabledVeteran === false;
    }),
  );

  setHead() {
    this.dirtyChecker2.dirtyChecker.setHead();
  }
}
